import moment from 'moment';
import { locationsStore } from '@/libs/locations/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { TargetsRepository } from '@/libs/tpm-analytics/data/targets.repository';
import { GlobalTargets, TargetRecord } from '@/libs/tpm-analytics/core/types';

export async function getGlobalTargets(fromIso: string, toIso: string): Promise<GlobalTargets> {
    const list = await TargetsRepository.fetchTargets();
    const from = moment(fromIso); const to = moment(toIso);
    const inRange = (t: TargetRecord) => {
        const f = t.fromDate ? moment(t.fromDate) : null;
        const tt = t.toDate ? moment(t.toDate) : null;
        const startOk = !f || f.isSameOrBefore(to);
        const endOk = !tt || tt.isSameOrAfter(from);
        return startOk && endOk && !t.workplaceId && !t.locationId;
    };
    const items = list.filter(inRange);
    if (items.length === 0) return { oee: null, availability: null, performance: null, quality: null };
    const t0 = items.sort((a, b) => (b.fromDate ? moment(b.fromDate).valueOf() : -Infinity) - (a.fromDate ? moment(a.fromDate).valueOf() : -Infinity))[0];
    const norm = (v: number | null | undefined) => (v == null || isNaN(v as any) ? null : ((v as number) > 1 ? (v as number) / 100 : (v as number)));
    return {
        oee: norm(t0.targetOee ?? null),
        availability: norm((t0 as any).targetOeeAvailability ?? null),
        performance: norm((t0 as any).targetOeePerformance ?? null),
        quality: norm((t0 as any).targetOeeQuality ?? null),
    } as GlobalTargets;
}

export async function getGlobalTargetOee(fromIso: string, toIso: string): Promise<number | null> {
    const targets = await TargetsRepository.fetchTargets();
    const from = moment(fromIso); const to = moment(toIso);
    const inRange = (t: TargetRecord) => {
        const f = t.fromDate ? moment(t.fromDate) : null;
        const tt = t.toDate ? moment(t.toDate) : null;
        const startOk = !f || f.isSameOrBefore(to);
        const endOk = !tt || tt.isSameOrAfter(from);
        return startOk && endOk;
    };
    const list = targets.filter((t) => inRange(t) && t.targetOee != null);
    if (list.length === 0) return null;
    const val = list[0].targetOee as number;
    return val > 1 ? val / 100 : val;
}

export async function getWorldClassTarget(fromIso: string, toIso: string): Promise<number | null> {
    const targets = await TargetsRepository.fetchTargets();
    const from = moment(fromIso);
    const to = moment(toIso);
    const inRange = (t: TargetRecord) => {
        const f = t.fromDate ? moment(t.fromDate) : null;
        const tt = t.toDate ? moment(t.toDate) : null;
        const startOk = !f || f.isSameOrBefore(to);
        const endOk = !tt || tt.isSameOrAfter(from);
        return startOk && endOk;
    };
    const items = targets.filter(inRange).filter(t => typeof t.worldClassOee === 'number');
    if (items.length === 0) return null;
    items.sort((a, b) => {
        const af = a.fromDate ? moment(a.fromDate).valueOf() : -Infinity;
        const bf = b.fromDate ? moment(b.fromDate).valueOf() : -Infinity;
        return bf - af;
    });
    const t0 = items[0] as any;
    const v = (t0.worldClassOee ?? null) as number | null;
    if (isNaN(v as any) || v == null) return null;
    return v > 1 ? v / 100 : v;
}

export async function getTargetsPerWorkplace(
    fromIso: string,
    toIso: string,
    workplaces: { workplaceId: string | null; locationId?: string | null }[]
): Promise<Map<string | null, { worldClass?: number | null; oeeTarget?: number | null }>> {
    const targets = await TargetsRepository.fetchTargets();

    const locParents = new Map<string, string | null>();
    try {
        const locations: any[] = (locationsStore.useGetter(CrudGetter.Data) as any[]) || [];
        for (const l of locations) {
            if (!l || !l.id) continue;
            locParents.set(l.id, l.parentalLocationId ?? null);
        }
    } catch { /* ignore */ }

    const inRange = (t: TargetRecord) => {
        const from = moment(fromIso);
        const to = moment(toIso);
        const f = t.fromDate ? moment(t.fromDate) : null;
        const tt = t.toDate ? moment(t.toDate) : null;
        const startOk = !f || f.isSameOrBefore(to);
        const endOk = !tt || tt.isSameOrAfter(from);
        return startOk && endOk;
    };

    const result = new Map<string | null, { worldClass?: number | null; oeeTarget?: number | null }>();
    for (const w of workplaces) {
        const wid = w.workplaceId ?? null;
        const locId = (w.locationId as string | null) ?? null;
        const candidates = targets.filter(inRange);
        let chosen: TargetRecord | null = null;
        const byWp = candidates.filter(t => t.workplaceId && t.workplaceId === wid);
        if (byWp.length > 0) {
            byWp.sort((a, b) => (a.fromDate ? moment(b.fromDate).valueOf() - moment(a.fromDate).valueOf() : 0));
            chosen = byWp[0];
        } else {
            let cur = locId;
            while (!chosen && cur) {
                const byLoc = candidates.filter(t => t.locationId && t.locationId === cur);
                if (byLoc.length > 0) {
                    byLoc.sort((a, b) => (a.fromDate ? moment(b.fromDate).valueOf() - moment(a.fromDate).valueOf() : 0));
                    chosen = byLoc[0];
                    break;
                }
                cur = locParents.get(cur) ?? null;
            }
            if (!chosen) {
                const global = candidates.filter(t => !t.locationId && !t.workplaceId);
                if (global.length > 0) {
                    global.sort((a, b) => (a.fromDate ? moment(b.fromDate).valueOf() - moment(a.fromDate).valueOf() : 0));
                    chosen = global[0];
                }
            }
        }
        const world = (chosen as any)?.worldClassOee ?? null;
        const goal = (chosen as any)?.targetOee ?? null;
        result.set(wid, { worldClass: world, oeeTarget: goal });
    }
    return result;
}


